import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, MailCheck, LogOut, Eye, EyeOff, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PasswordStrength } from "@/components/security/PasswordStrength";
import { passwordMeetsPolicy } from "@/lib/passwordStrength";

function PwInput({ id, value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={show ? "text" : "password"} value={value} onChange={onChange}
        placeholder={placeholder} autoComplete={id === "current" ? "current-password" : "new-password"}
        className="pr-10" data-testid={testid} />
      <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        aria-label={show ? "Hide password" : "Show password"} data-testid={`${testid}-toggle`}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function SecurityPanel({ email }) {
  const { updatePasswordSecure, resetPassword, signOutOtherSessions } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [sessionBusy, setSessionBusy] = useState(false);

  const canSubmit = current && passwordMeetsPolicy(next) && next === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!passwordMeetsPolicy(next)) return toast.error("Your new password doesn’t meet the requirements yet.");
    if (next !== confirm) return toast.error("The new passwords don’t match.");
    if (next === current) return toast.error("Please choose a password different from your current one.");
    setBusy(true);
    const { error } = await updatePasswordSecure({ currentPassword: current, newPassword: next });
    setBusy(false);
    if (error) return toast.error(error.message || "We couldn’t update your password. Please try again.");
    setCurrent(""); setNext(""); setConfirm(""); setDone(true);
    toast.success("Your password has been changed");
  };

  const sendReset = async () => {
    if (!email) return;
    setResetBusy(true);
    await resetPassword(email);
    setResetBusy(false);
    toast.success("Reset link sent", { description: `If an account exists for ${email}, a secure link is on its way.` });
  };

  const signOutOthers = async () => {
    setSessionBusy(true);
    const { error } = await signOutOtherSessions();
    setSessionBusy(false);
    if (error) return toast.error(error.message || "Couldn’t sign out other sessions.");
    toast.success("Signed out of all other sessions", { description: "This device stays signed in." });
  };

  return (
    <div className="space-y-6" data-testid="security-panel">
      {/* change password */}
      <Card className="p-6 shadow-soft">
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary"><KeyRound className="h-4.5 w-4.5" /></span>
          <div>
            <h3 className="font-heading text-lg font-semibold">Change password</h3>
            <p className="text-xs text-muted-foreground">Set a strong, permanent password for your account.</p>
          </div>
        </div>

        {done && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-sm text-success" data-testid="pw-success">
            <CheckCircle2 className="h-4 w-4" /> Password changed successfully. Use it next time you sign in.
          </div>
        )}

        <form onSubmit={submit} className="max-w-md space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="current">Current password</Label>
            <PwInput id="current" value={current} onChange={(e) => { setCurrent(e.target.value); setDone(false); }} testid="pw-current" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new">New password</Label>
            <PwInput id="new" value={next} onChange={(e) => { setNext(e.target.value); setDone(false); }} testid="pw-new" />
            <PasswordStrength value={next} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm new password</Label>
            <PwInput id="confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="pw-confirm" />
            {confirm && confirm !== next && <p className="text-xs text-destructive" data-testid="pw-mismatch">Passwords don’t match yet.</p>}
          </div>
          <Button type="submit" disabled={!canSubmit} className="gap-1.5" data-testid="pw-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </Button>
        </form>
      </Card>

      {/* forgot / reset by email */}
      <Card className="flex flex-wrap items-center justify-between gap-3 p-6 shadow-soft">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary"><MailCheck className="h-4.5 w-4.5" /></span>
          <div>
            <h3 className="font-heading text-lg font-semibold">Forgot your password?</h3>
            <p className="max-w-md text-xs text-muted-foreground">We’ll email a secure link to <span className="font-medium text-foreground">{email}</span> so you can set a new one without your current password.</p>
          </div>
        </div>
        <Button variant="outline" className="gap-1.5" onClick={sendReset} disabled={resetBusy} data-testid="pw-send-reset">
          {resetBusy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset email
        </Button>
      </Card>

      {/* sessions */}
      <Card className="flex flex-wrap items-center justify-between gap-3 p-6 shadow-soft">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary"><LogOut className="h-4.5 w-4.5" /></span>
          <div>
            <h3 className="font-heading text-lg font-semibold">Sign out other sessions</h3>
            <p className="max-w-md text-xs text-muted-foreground">Signs you out everywhere except this device — useful after changing your password or using a shared computer.</p>
          </div>
        </div>
        <Button variant="outline" className="gap-1.5" onClick={signOutOthers} disabled={sessionBusy} data-testid="pw-signout-others">
          {sessionBusy && <Loader2 className="h-4 w-4 animate-spin" />} Sign out other sessions
        </Button>
      </Card>

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-accent" /> Passwords are handled securely by Supabase Auth. Nirvana never stores or displays your password — not even administrators.
      </p>
    </div>
  );
}
